// Default row for data / ad_hoc / expiry / holiday events.

import { useMemo, useState } from "react";
import type { CalendarDataResponse } from "../../types";
import { formatFigure, numberFrom } from "../../utils/outcomes";
import { MiniChart, type MiniChartPoint } from "../MiniChart";
import { Flag } from "../Flag";
import { ImportanceIcon } from "../ImportanceIcon";
import { Markdown } from "../Markdown";
import { TimeCell } from "../TimeCell";
import {
  computeDataRowClasses,
  getCategory,
  hasDetail,
  useChangeAnimation,
  type CommonRowProps,
} from "../rowCommon";

export function DataRow(props: CommonRowProps) {
  useChangeAnimation(props);
  const [expanded, setExpanded] = useState(false);

  const event = props.event as CalendarDataResponse;
  const category = getCategory(props.event);
  const detail = (props.event as { detail?: string | null }).detail;
  const isHoliday = props.event.classification === "holiday";

  const location =
    category?.location || (props.event as { location?: string }).location || "";

  const info =
    category?.display_name ||
    category?.name ||
    (props.event as { summary?: string }).summary ||
    "";
  const surveyDate = (props.event as { survey_date?: string | null }).survey_date;

  const currency = category?.currency;
  const unit = category?.unit;
  const mult = event.multiplier;
  const figures = {
    actual: formatFigure(event.actual, currency, unit, mult),
    expected: formatFigure(event.expected, currency, unit, mult),
    low: props.showLowHigh ? formatFigure(event.low, currency, unit, mult) : "-",
    high: props.showLowHigh ? formatFigure(event.high, currency, unit, mult) : "-",
    previous: formatFigure(event.previous, currency, unit, mult),
    revised: formatFigure(event.revised, currency, unit, mult),
  };

  const detailSpan =
    (props.showLowHigh ? 8 : 6) + (props.chart === "mini" ? 1 : 0);

  // Sparkline points for this category (ascending, past-only) in chart mode.
  // History includes upcoming releases, so future-dated entries are dropped.
  const historyPoints = useMemo<MiniChartPoint[]>(() => {
    if (props.chart !== "mini" || props.event.classification !== "data") return [];
    const id = (props.event as { category_id?: number | null }).category_id;
    if (id == null) return [];
    const series = props.categoryHistory?.[String(id)];
    if (!series) return [];
    const nowSeconds = Date.now() / 1000;
    const occurs = series.occurs_at ?? [];
    const actuals = series.actual ?? [];
    const points: MiniChartPoint[] = [];
    for (let i = 0; i < occurs.length; i++) {
      const occursAt = occurs[i];
      const actual = actuals[i];
      if (occursAt == null || actual == null) continue;
      const y = numberFrom(actual);
      const x = Date.parse(occursAt) / 1000;
      if (y === undefined || Number.isNaN(x) || x > nowSeconds) continue;
      points.push({ x, y, date: occursAt.slice(0, 10), label: actual });
    }
    return points;
  }, [props.chart, props.event, props.categoryHistory]);

  return (
    <>
      <div className={computeDataRowClasses(props) + " calendar-grid-row"}>
        <div className="cell-location">
          <span className="flag-container">
            <Flag region={location} />
          </span>
        </div>
        <div className="cell-time">
          <TimeCell event={props.event} />
        </div>
        <div className="cell-title">
          {hasDetail(props.event) && (
            <button onClick={() => setExpanded((v) => !v)} className="expand-toggle">
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <div className="info">
            {(props.event as { te_sourced?: boolean }).te_sourced && (
              <span className="te-logo">TE</span>
            )}
            <span className="event-name">{info}</span>
            {category?.period && <span className="period"> - {category.period}</span>}
            {surveyDate && <span className="survey-date"> - ({surveyDate})</span>}
          </div>
        </div>
        {isHoliday ? (
          <>
            <div className="cell-actual">&nbsp;</div>
            {props.chart === "mini" && <div className="cell-chart">&nbsp;</div>}
            <div className="cell-expected">&nbsp;</div>
            {props.showLowHigh && (
              <>
                <div className="cell-low">&nbsp;</div>
                <div className="cell-high">&nbsp;</div>
              </>
            )}
            <div className="cell-previous">&nbsp;</div>
            <div className="cell-revised">&nbsp;</div>
            <div className="cell-importance">
              <ImportanceIcon importance={category?.importance} size="md" />
            </div>
          </>
        ) : (
          <>
            <div className="cell-actual">
              <span className="figure">{figures.actual}</span>
            </div>
            {props.chart === "mini" && (
              <div className="cell-chart">
                {historyPoints.length > 1 ? (
                  <MiniChart points={historyPoints} />
                ) : (
                  <>&nbsp;</>
                )}
              </div>
            )}
            <div className="cell-expected">{figures.expected}</div>
            {props.showLowHigh && (
              <>
                <div className="cell-low">{figures.low}</div>
                <div className="cell-high">{figures.high}</div>
              </>
            )}
            <div className="cell-previous">{figures.previous}</div>
            <div className="cell-revised">{figures.revised}</div>
            <div className="cell-importance">
              <ImportanceIcon importance={category?.importance} size="md" />
            </div>
          </>
        )}
      </div>
      {expanded && detail && (
        <div className="detail-row calendar-grid-row">
          <div className="cell-location">&nbsp;</div>
          <div className="cell-time">&nbsp;</div>
          <div className="cell-title" style={{ gridColumn: `span ${detailSpan}` }}>
            <div className="detail-content">
              <Markdown content={detail} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DataRow;
